import React, { useCallback } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { token } from '@atlaskit/tokens';

import { MorphingShapeLoader } from '@atlassian/rovo-platform-ui-components/primitives/MorphingShapeLoader';
import { RainbowProgressBar } from '@atlassian/rovo-platform-ui-components/primitives/RainbowProgressBar';
import { EllipsesAnimation } from '@atlassian/rovo-platform-ui-components/primitives/EllipsesAnimation';
import { ShimmerText } from '@atlassian/rovo-platform-ui-components/primitives/ShimmerText';

import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import { useCancelledAnalytics } from '../analytics.ts';
import {
	useRotatingLoadingMessage,
	useSuggestionsLoadingMessages,
} from '../useRotatingLoadingMessage.ts';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		height: '100%',
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	loadingContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		width: '80%',
	},
});

const i18n = defineMessages({
	cancelButton: {
		id: 'suggestions-panel.loading-state.cancel-button',
		defaultMessage: 'Stop suggesting',
		description:
			'Button label to cancel/stop Rovo from generating suggestions in the suggestions panel',
	},
});

type SuggestionsLoadingStateProps = {
	/**
	 * Name of the agent doing the review, used to name it in the first loading message.
	 * Falls back to the generic Rovo copy when absent.
	 */
	agentName?: string;
	api?: FullPageEditorPresetAPI;
	/**
	 * Epoch ms when the thinking session started (from shared plugin state). Anchors the
	 * message rotation so this state and the inline loading bar stay on the same message.
	 */
	startedAt?: number | null;
};

export const SuggestionsLoadingState = ({
	agentName,
	api,
	startedAt,
}: SuggestionsLoadingStateProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const loadingMessages = useSuggestionsLoadingMessages({ agentName });
	const loadingMessage = useRotatingLoadingMessage(loadingMessages, startedAt);
	const fireCancelledAnalytics = useCancelledAnalytics();

	const handleCancel = useCallback(() => {
		fireCancelledAnalytics();
		api?.aiSuggestions?.actions?.setThinkingState('idle');
	}, [api, fireCancelledAnalytics]);

	return (
		<div css={styles.container} data-testid="suggestions-panel-loading-state">
			<div css={styles.loadingContainer}>
				<MorphingShapeLoader testId="suggestions-panel-thinking-indicator" size="large" />
				<ShimmerText size="medium">
					{loadingMessage}
					<EllipsesAnimation isAnimated />
				</ShimmerText>
				<RainbowProgressBar testId="suggestions-panel-progress-bar" label={loadingMessage} />
			</div>
			<Button onClick={handleCancel}>{formatMessage(i18n.cancelButton)}</Button>
		</div>
	);
};
